import { useState, useContext, useEffect } from "react";
import CustomButton from "./CustomButton";
import { useNavigate, useParams } from "react-router-dom";
import { DollarContext } from "../contexts/DollarContext";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import api from "../services/api";
import SpinnerLoading from "./SpinnerLoading";
import PdfUploadDownloadPayment from "./PdfUploadDownloadPayment";
import { v4 as uuidv4 } from "uuid";
import useAttachmentsStore from "../store/attachmentsStore";
import { uploadFile } from "../axios/uploadFiles";
import SpinnerLoadingBlack from "./SpinnerLoadingBlack";

const Modal = ({ isOpen, onClose, message, type }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
      <div
        className={`bg-white p-6 rounded shadow-md w-96 text-center ${
          type === "success" ? "border-green-500" : "border-red-500"
        } border`}
      >
        <p className="text-lg font-semibold mb-4">
          {type === "success" ? "Sucesso" : "Erro"}
        </p>
        <p className="text-gray-700 mb-6">{message}</p>
        <button
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          onClick={onClose}
        >
          Fechar
        </button>
      </div>
    </div>
  );
};

const TableEditPayment = ({ payment }) => {
  const dollarPriceToday = useContext(DollarContext);
  const [isModalSuccessOpen, setIsModalSuccessOpen] = useState(false);
  const [isModalErrorOpen, setIsModalErrorOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { addAttachment, attachments, clearAttachments } =
    useAttachmentsStore();
  const [isLoadingPdfUpload, setIsLoadingPdfUpload] = useState(false);
  const navigate = useNavigate();
  const { id, orderId } = useParams();

  const paymentSchema = z.object({
    paymentAmount: z
      .string()
      .refine(
        (value) => !isNaN(parseFloat(value)) && parseFloat(value) > 0,
        "O valor deve ser um número positivo e obrigatório"
      ),
    exchangeRate: z
      .string()
      .refine(
        (value) => !isNaN(parseFloat(value)) && parseFloat(value) > 0,
        "O valor deve ser um número positivo e obrigatório"
      ),

    description: z
      .string()
      .min(1, "A descrição deve ter pelo menos 1 caractere"),
    paymentDate: z.preprocess(
      (value) => (typeof value === "string" ? value : ""),
      z
        .string()
        .min(1, "O preenchimento da data é obrigatório")
        .refine((value) => !isNaN(Date.parse(value)), "A data deve ser válida")
    ),
  });

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        alert("Por favor, selecione um arquivo PDF.");
        return;
      }

      const MAX_FILE_SIZE = 5 * 1024 * 1024;
      if (file.size > MAX_FILE_SIZE) {
        alert("O arquivo excede o tamanho máximo permitido de 5MB.");
        return;
      }

      const tempReferenceId = uuidv4();
      setIsLoadingPdfUpload(true);
      try {
        const response = await uploadFile(file);
        const dbAttachmentsPost = {
          google_id: response.result.id,
          reference_id: tempReferenceId,
          reference_type: "TEMP",
          attachment_type: "PAYMENT_ATTACHMENT",
          attachment_description: response.result.name,
          privacy: "PUBLIC",
        };
        await api.post(`/attachments/create-attachment`, dbAttachmentsPost);

        addAttachment(dbAttachmentsPost.reference_id);
        const currentAttachments = useAttachmentsStore.getState().attachments;
        console.log("Estado atualizado no Zustand:", currentAttachments);

        console.log("Resposta da API:", response);
      } catch (error) {
        console.error("Erro ao enviar o arquivo:", error);
        alert(
          "Erro ao enviar o arquivo. Habilite o pop-up no seu navegador, ou sua conexão com a internet."
        );
      } finally {
        setIsLoadingPdfUpload(false);
      }
    }
  };

  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: zodResolver(paymentSchema),
  });

  useEffect(() => {
    setValue("description", payment.description);
    setValue("exchangeRate", payment.exchange_rate);
    setValue("paymentAmount", payment.payment_amount);

    if (payment.payment_date) {
      const formattedDate = new Date(payment.payment_date)
        .toISOString()
        .split("T")[0];
      setValue("paymentDate", formattedDate);
    }

    clearAttachments();
  }, [payment]);

  const onFormSubmit = async (values) => {
    setIsLoading(true);
    try {
      const payment = { orderId, ...values };
      await api.put(`/payments/${id}`, payment);

      if (attachments.length > 0) {
        await api.put("/attachments/update-reference", {
          temp_reference_ids: attachments,
          order_id: orderId,
          reference_type: "PAYMENT",
        });

        clearAttachments();
      }

      setModalMessage("Pagamento atualizado com sucesso!");
      setIsModalSuccessOpen(true);
    } catch (error) {
      setModalMessage(
        error.status !== 500
          ? error.response.data.error
          : "Erro ao atualizado pagamento. Verifique os dados e tente novamente."
      );
      setIsModalErrorOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <form
        className="flex flex-col justify-between min-h-[calc(100vh-350px)]"
        onSubmit={handleSubmit(onFormSubmit)}
      >
        <div className="flex flex-col gap-4">
          <div>
            <label htmlFor="paymentDate" className="block mb-2">
              Data
            </label>
            <input
              id="paymentDate"
              type="date"
              className="w-full h-[46px] px-4 border border-gray-300 rounded"
              {...register("paymentDate")}
            />
            {errors.paymentDate && (
              <p className="text-red-500 text-sm mt-1">
                {errors.paymentDate.message}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="description" className="block mb-2">
              Descrição
            </label>
            <input
              id="description"
              type="text"
              placeholder="Adicione uma descrição"
              className="w-full h-[46px] px-4 border border-gray-300 rounded"
              {...register("description")}
            />
            {errors.description && (
              <p className="text-red-500 text-sm mt-1">
                {errors.description.message}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="dollar" className="block mb-2">
              Dólar
            </label>
            <input
              id="dollar"
              type="number"
              step="0.01"
              placeholder={dollarPriceToday}
              className="w-full h-[46px] px-4 border border-gray-300 rounded bg-gray-100"
              {...register("exchangeRate")}
            />
            {errors.exchangeRate && (
              <p className="text-red-500 text-sm mt-1">
                {errors.exchangeRate.message}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="paymentAmount" className="block mb-2">
              Valor
            </label>
            <input
              id="paymentAmount"
              type="number"
              step="0.01"
              placeholder="$0,00"
              className="w-full h-[46px] px-4 border border-gray-300 rounded"
              {...register("paymentAmount")}
            />
            {errors.paymentAmount && (
              <p className="text-red-500 text-sm mt-1">
                {errors.paymentAmount.message}
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-start mt-6">
          <div className="w-[340px] h-[66px]">
            {isLoadingPdfUpload ? (
              <SpinnerLoadingBlack>Enviando...</SpinnerLoadingBlack>
            ) : (
              <PdfUploadDownloadPayment onUpload={handleFileUpload} />
            )}
          </div>
        </div>

        <div className="flex justify-center mt-6">
          <div className="w-[340px] h-[66px]">
            <CustomButton
              className="text-2xl bg-primary text-white w-full h-full rounded"
              type="submit"
            >
              {isLoading ? <SpinnerLoading /> : "Salvar"}
            </CustomButton>
          </div>
        </div>
      </form>

      {/* Modal de Sucesso */}
      <Modal
        isOpen={isModalSuccessOpen}
        onClose={() => {
          setIsModalSuccessOpen(false);
          navigate("/pedidos");
        }}
        message={modalMessage}
        type="success"
      />

      {/* Modal de Erro */}
      <Modal
        isOpen={isModalErrorOpen}
        onClose={() => setIsModalErrorOpen(false)}
        message={modalMessage}
        type="error"
      />
    </>
  );
};

export default TableEditPayment;
